import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import type { DocumentNode } from "@apollo/client";
import Button from "@/components/Button";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import Dialog from "@/components/Dialog";
import ListItem from "@/components/ListItem";
import { NotePriorityValues } from "@/types";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import type { FacilityGovernancePerson, GovernanceTitle } from "./types";
import {
  UPDATE_FACILITY_GOVERNANCE_ROLE_MUTATION,
  type UpdateFacilityGovernanceRoleMutationData,
  type UpdateFacilityGovernanceRoleMutationVariables
} from "./mutations";
import { FACILITY_GOVERNANCE_ROLES_QUERY, FACILITY_GOVERNANCE_TITLES_QUERY } from "./queries";
import {
  buildGovernanceTitleOptions,
  getGovernanceTitleFieldState,
  GOVERNANCE_ROLE_UPDATED_NOTE_TITLE,
  formatSectionDisplayName
} from "./_utils";

interface EditGovernanceRoleFacilitySubject {
  name: string;
  section?: { name: string } | null;
}

export interface EditGovernanceRoleSubject {
  id: string;
  title: GovernanceTitle;
  person?: FacilityGovernancePerson;
}

interface EditGovernanceRoleModalProps {
  open: boolean;
  role: EditGovernanceRoleSubject | null;
  onClose: () => void;
  refetchQueries?: DocumentNode[];
  facility?: EditGovernanceRoleFacilitySubject | null;
}

interface FormValues {
  governanceTitleId: string;
  internalNote: string;
}

interface SubmitEditGovernanceRoleArgs {
  associationId: string;
  values: FormValues;
  updateFacilityGovernanceRole: (options: {
    variables: UpdateFacilityGovernanceRoleMutationVariables;
  }) => Promise<{ data?: UpdateFacilityGovernanceRoleMutationData | null }>;
}

export async function submitEditGovernanceRole({
  associationId,
  values,
  updateFacilityGovernanceRole
}: SubmitEditGovernanceRoleArgs): Promise<boolean> {
  const trimmedNote = values.internalNote.trim();

  const result = await updateFacilityGovernanceRole({
    variables: {
      input: {
        id: associationId,
        facilityGovernanceTitleId: values.governanceTitleId,
        note: trimmedNote
          ? { content: trimmedNote, priority: NotePriorityValues.NONE, title: GOVERNANCE_ROLE_UPDATED_NOTE_TITLE }
          : null
      }
    }
  });

  if (!result.data?.updateFacilityGovernanceRole.success) {
    showErrorSnackbar(result.data?.updateFacilityGovernanceRole.message || "Unable to update governance role.");
    return false;
  }

  showSnackbar("Governance role updated");
  return true;
}

export function EditGovernanceRoleModal({
  open,
  role,
  onClose,
  refetchQueries = [FACILITY_GOVERNANCE_ROLES_QUERY],
  facility
}: EditGovernanceRoleModalProps) {
  const [updateFacilityGovernanceRole, { loading: saving }] = useMutation(UPDATE_FACILITY_GOVERNANCE_ROLE_MUTATION, {
    refetchQueries
  });
  const {
    data: titlesData,
    loading: titlesLoading,
    error: titlesError
  } = useQuery(FACILITY_GOVERNANCE_TITLES_QUERY, { fetchPolicy: "cache-first", skip: !open || !role });
  const governanceTitleOptions = buildGovernanceTitleOptions(titlesData?.facilityGovernanceTitles);
  const titleFieldState = getGovernanceTitleFieldState({
    loading: titlesLoading,
    hasError: Boolean(titlesError),
    optionCount: governanceTitleOptions.length
  });

  const { control, handleSubmit, reset } = useForm<FormValues>({
    values: {
      governanceTitleId: role?.title.id ?? "",
      internalNote: ""
    }
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    if (!role) {
      return;
    }

    try {
      const success = await submitEditGovernanceRole({
        associationId: role.id,
        values,
        updateFacilityGovernanceRole
      });
      if (success) {
        handleClose();
      }
    } catch {
      showErrorSnackbar("Unable to update governance role.");
    }
  };

  if (!role) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Edit Governance Role"
      className="w-[640px]"
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={saving}>
            Save
          </Button>
        </>
      }
    >
      <div key={role.id} className="space-y-6">
        {facility ? (
          <ListItem
            compact
            iconName="store"
            iconBackground
            headline={facility.name}
            supportingText={formatSectionDisplayName(facility.section?.name)}
          />
        ) : role.person ? (
          <ListItem
            useAvatar
            compact
            picture={role.person.profilePhoto}
            iconName="person"
            overline={role.person.pgaId || undefined}
            headline={role.person.displayName}
            supportingText={role.person.latestProgram?.programType?.name || undefined}
          />
        ) : null}

        <Controller
          name="governanceTitleId"
          control={control}
          rules={{ required: "Role is required" }}
          render={({ field: { onChange, value }, fieldState }) => (
            <Select
              label="Role"
              options={governanceTitleOptions}
              value={value}
              onSelect={onChange}
              disabled={titleFieldState.disabled}
              error={fieldState.invalid || Boolean(titleFieldState.errorText)}
              errorText={titleFieldState.errorText ?? fieldState.error?.message}
              supportingText={titleFieldState.supportingText}
              required
            />
          )}
        />

        <Controller
          name="internalNote"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="textarea"
              rows={3}
              label="Internal Note (Optional)"
              value={value}
              onChangeText={onChange}
              className="w-full"
            />
          )}
        />
      </div>
    </Dialog>
  );
}
